// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { NavigationControls } from '@client/banks/layouts/navigation-controls';
import { ScreenLayout } from '@client/banks/layouts/screen-layout';
import { Chip, Typography } from '@mui/material';
import { AppIcon } from '@shared/ui-kit/components/app-icon';
import type { SvgIconName } from '@shared/ui-kit/components/app-icon/app-icon';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import catalogStyles from '../styles/institution-catalog.module.css';
import styles from './flow-select.module.css';
import { type BankUploadFlowOption, useFlowSelect } from './use-flow-select';

type FlowFeature = {
  icon: SvgIconName;
  i18nKey: string;
};

type FlowCardConfig = {
  id: BankUploadFlowOption;
  tileIcon: SvgIconName;
  tileClassName: string;
  titleKey: string;
  tagKey: string;
  tagColor: 'info' | 'warning';
  features: readonly FlowFeature[];
  testId: string;
};

const FLOW_CARDS: readonly FlowCardConfig[] = [
  {
    id: 'wealthreader',
    tileIcon: 'Globe',
    tileClassName: styles['flow-select-card-icon-wealthreader'],
    titleKey: 'aggregator.create-bank.flow-select.wealthreader.title',
    tagKey: 'aggregator.create-bank.flow-select.wealthreader.tag',
    tagColor: 'info',
    features: [
      {
        icon: 'Shield',
        i18nKey: 'aggregator.create-bank.flow-select.wealthreader.feature-1',
      },
      {
        icon: 'CheckCircle',
        i18nKey: 'aggregator.create-bank.flow-select.wealthreader.feature-2',
      },
      {
        icon: 'Landmark',
        i18nKey: 'aggregator.create-bank.flow-select.wealthreader.feature-3',
      },
    ],
    testId: 'bank-upload-flow-wealthreader',
  },
  {
    id: 'manual',
    tileIcon: 'FilePen',
    tileClassName: styles['flow-select-card-icon-manual'],
    titleKey: 'aggregator.create-bank.flow-select.manual.title',
    tagKey: 'aggregator.create-bank.flow-select.manual.tag',
    tagColor: 'warning',
    features: [
      {
        icon: 'CheckCircle',
        i18nKey: 'aggregator.create-bank.flow-select.manual.feature-1',
      },
      {
        icon: 'Landmark',
        i18nKey: 'aggregator.create-bank.flow-select.manual.feature-2',
      },
      {
        icon: 'Info',
        i18nKey: 'aggregator.create-bank.flow-select.manual.feature-3',
      },
    ],
    testId: 'bank-upload-flow-manual',
  },
];

export const FlowSelect = defineComponent({
  displayName: 'FlowSelect',
  useSetup: useFlowSelect,
  View: ({
    hasDraft,
    selectedFlow,
    onSelectFlow,
    onContinue,
    isContinueDisabled,
  }) => (
    <ScreenLayout
      className={styles['flow-select-layout']}
      actions={
        <NavigationControls
          hidePrevious
          nextDisabled={isContinueDisabled}
          nextLabel={<Translation i18nKey="common.continue" ns="common" />}
          onNext={onContinue}
        />
      }
    >
      <div className={styles['flow-select']} data-testid="bank-upload-flow-select">
        <header className={catalogStyles['step-header']}>
          <Typography
            component="h2"
            variant="h6"
            className={catalogStyles['step-title']}
          >
            <Translation
              i18nKey="aggregator.create-bank.flow-select.title"
              ns="aggregator"
            />
          </Typography>
          <Typography variant="body2" className={catalogStyles['step-description']}>
            <Translation
              i18nKey="aggregator.create-bank.flow-select.subtitle"
              ns="aggregator"
            />
          </Typography>
        </header>

        <div className={styles['flow-select-options']} role="radiogroup">
          {FLOW_CARDS.map(card => {
            const isSelected = selectedFlow === card.id;

            return (
              <button
                key={card.id}
                type="button"
                role="radio"
                aria-checked={isSelected}
                className={classNames(
                  styles['flow-select-card'],
                  isSelected && styles['flow-select-card-selected'],
                )}
                onClick={() => onSelectFlow(card.id)}
                data-testid={card.testId}
              >
                <span
                  className={classNames(
                    styles['flow-select-card-icon'],
                    card.tileClassName,
                  )}
                >
                  <AppIcon icon={card.tileIcon} size="lg" color="light" />
                </span>

                <div className={styles['flow-select-card-body']}>
                  <div className={styles['flow-select-card-title-row']}>
                    <Typography className={styles['flow-select-card-title']}>
                      <Translation i18nKey={card.titleKey as any} ns="aggregator" />
                    </Typography>
                    <Chip
                      size="small"
                      color={card.tagColor}
                      className={styles['flow-select-card-tag']}
                      label={
                        <Translation i18nKey={card.tagKey as any} ns="aggregator" />
                      }
                    />
                  </div>

                  <ul className={styles['flow-select-card-features']}>
                    {card.features.map(feature => (
                      <li
                        key={feature.i18nKey}
                        className={styles['flow-select-card-feature']}
                      >
                        <AppIcon
                          icon={feature.icon}
                          size="base"
                          color="success"
                          className={styles['flow-select-card-feature-icon']}
                        />
                        <Translation
                          i18nKey={feature.i18nKey as any}
                          ns="aggregator"
                        />
                      </li>
                    ))}
                  </ul>

                  {hasDraft && card.id === 'manual' ? (
                    <Typography
                      variant="body2"
                      className={styles['flow-select-draft-hint']}
                      data-testid="bank-upload-flow-manual-draft-hint"
                    >
                      <Translation
                        i18nKey="aggregator.create-bank.flow-select.manual.draft-hint"
                        ns="aggregator"
                      />
                    </Typography>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </ScreenLayout>
  ),
});
